import { useEffect, useMemo, useState } from 'react'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { EpisodeCard } from '@/components/EpisodeCard'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import type { Episode, Difficulty } from '@/domain/episode'

const DIFFICULTIES: Array<Difficulty | 'all'> = ['all', 'beginner', 'intermediate', 'advanced']

export function EpisodeLibraryPage() {
  const { episodes, results } = useServices()
  const { currentUser } = useAuth()
  const [allEpisodes, setAllEpisodes] = useState<Episode[]>([])
  const [passedEpisodeIds, setPassedEpisodeIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [difficulty, setDifficulty] = useState<Difficulty | 'all'>('all')
  const [topic, setTopic] = useState<string>('all')

  useEffect(() => {
    if (!currentUser) return
    Promise.all([episodes.list(), results.listForUser(currentUser.id)]).then(([eps, res]) => {
      setAllEpisodes(eps)
      setPassedEpisodeIds(new Set(res.filter((r) => r.passed).map((r) => r.episodeId)))
      setLoading(false)
    })
  }, [episodes, results, currentUser])

  const topics = useMemo(() => {
    const set = new Set<string>()
    allEpisodes.forEach((e) => e.topicTags.forEach((t) => set.add(t)))
    return ['all', ...Array.from(set)]
  }, [allEpisodes])

  const filtered = allEpisodes.filter((e) => {
    if (difficulty !== 'all' && e.difficulty !== difficulty) return false
    if (topic !== 'all' && !e.topicTags.includes(topic)) return false
    if (search && !`${e.title} ${e.synopsis}`.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-display text-3xl font-bold text-white">Episode Library</h1>
      <p className="mt-2 text-white/60">Animated stories that teach real cybersecurity habits.</p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          placeholder="Search episodes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-teal-400 focus:outline-none sm:max-w-xs"
        />
        <select
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value as Difficulty | 'all')}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-teal-400 focus:outline-none"
        >
          {DIFFICULTIES.map((d) => (
            <option key={d} value={d} className="bg-navy-900">
              {d === 'all' ? 'All difficulties' : d[0].toUpperCase() + d.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-teal-400 focus:outline-none"
        >
          {topics.map((t) => (
            <option key={t} value={t} className="bg-navy-900">
              {t === 'all' ? 'All topics' : `#${t.replace(/-/g, ' ')}`}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <LoadingSpinner label="Loading episodes…" />
      ) : filtered.length === 0 ? (
        <div className="cyber-panel mt-10 p-10 text-center text-white/60">
          No episodes match your filters yet.
        </div>
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((ep) => (
            <EpisodeCard key={ep.id} episode={ep} passed={passedEpisodeIds.has(ep.id)} />
          ))}
        </div>
      )}
    </div>
  )
}
